import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { Typography, Button, Link, Alert } from '@one-thd/sui-atomic-components';
import { createBrowserHistory } from 'history';
import classNames from 'classnames';
import BalanceCard from './BalanceCard';
import {
  URL,
  VERBIAGE,
  NUCLEUS_CHECKOUT_PATH,
  containerWidthClass,
  FEATURE_SWITCH
} from '../../utils/consts';
import { getAddToCartPayload, isLocalHost } from '../../utils/utils';
import SelectAmount from './SelectAmount';
import { setAnalyticsData, setATCAnalytics } from '../../utils/Analytics';
import { useCart } from '../../hooks/useCart';
import { publishNewRelicPageAction } from '../../utils/new-relic';

const ReloadForm = ({
  cardNumber,
  cardLast4,
  reloadableAmount,
  availableAmount,
  errorMsg,
  isDrawer,
  savedPaymentId,
  onCancel,
  onError
}) => {
  const [showAmount, setShowAmount] = useState(!!savedPaymentId);
  const { channel } = useContext(ExperienceContext);
  const isReloadEnabled = !!useConfigService(FEATURE_SWITCH.RELOAD_UI);
  const isDisabled = !!errorMsg || !isReloadEnabled;
  const isMobile = channel === 'mobile';
  const [addToCart, atcResponse] = useCart();
  const { loading: isAtcLoading, error: atcError, called: isAtcCalled } = atcResponse;
  const disableBtn = isAtcLoading && !atcError;
  const isGraphQlLoading = isAtcLoading && isAtcCalled;

  useEffect(() => {
    setAnalyticsData();
  }, []);

  const navigateToCheckout = () => {
    const history = createBrowserHistory();
    history.push(NUCLEUS_CHECKOUT_PATH);
    history.go();
  };

  const handleAmountChange = () => {
    if (isDisabled) {
      onError('');
    }
  };

  const handleNextStep = (cartId) => {
    const reloadCheckout = {
      cartId,
      experience: 'giftcards-reload',
      previousPath: window.location.pathname
    };
    window.sessionStorage.setItem('reloadCheckout', JSON.stringify(reloadCheckout));
    navigateToCheckout();
  };

  const handleAddToCart = async (event, amount) => {
    publishNewRelicPageAction({
      actionName: 'submitReloadButtonClicked',
      actionObject: { sectionName: 'reloadSection' }
    });
    if (isLocalHost() && typeof window !== 'undefined') {
      window.alert('Reload Successful');
    } else {
      event.preventDefault();
      const cartRequestData = getAddToCartPayload(
        amount,
        availableAmount,
        cardNumber,
        savedPaymentId,
        cardLast4
      );
      try {
        const response = await addToCart({
          cartRequest: cartRequestData,
          requestContext: {
            experience: {
              type: 'GC_R'
            }
          }
        });
        const { cartId } = response.addToCart;
        publishNewRelicPageAction({
          actionName: 'submitReloadButtonSuccess',
          actionObject: {
            sectionName: 'reloadSection',
            message: 'Reload amount ATC success'
          }
        });
        setATCAnalytics(cartId, amount);
        if (cartId) {
          handleNextStep(cartId);
        }
      } catch (error) {
        onError(VERBIAGE.technicalError);
        publishNewRelicPageAction({
          actionName: 'submitReloadButtonFail',
          actionObject: {
            sectionName: 'reloadSection',
            message: 'Reload amount ATC fail - exceptions'
          }
        });
      }
    }
  };

  const handleShowReload = () => {
    setShowAmount(true);
  };

  const handleCheckAnotherBalance = () => {
    onCancel();
  };

  const wrapperClassName = classNames(
    'sui-flex sui-flex-col sui-items-center sui-justify-between',
    {
      'sui-h-full': isDrawer
    }
  );

  const buttonWrapperClass = 'sui-mt-3 sui-w-full';

  return (
    <div className={wrapperClassName} data-testid="reload-form-wrapper">
      {!isDrawer && (
        <div className="sui-w-full sui-mb-8 sui-px-1 sui-mt-2" data-testid="cbr-title">
          <Typography variant={isMobile ? 'h3' : 'h1'} weight="bold" align="left">
            {VERBIAGE.checkBalanceAndReload}
          </Typography>
        </div>
      )}
      <div className={`sui-max-w-full ${containerWidthClass}`}>
        <BalanceCard cardNumber={cardNumber || cardLast4} availableAmount={availableAmount} />
        {errorMsg && (
          <div className="sui-mt-4">
            <Alert status="error" data-testid="card-number-Errormessage">
              {errorMsg}
            </Alert>
          </div>
        )}
      </div>
      {showAmount && (
        <SelectAmount
          availableAmount={availableAmount}
          reloadableAmount={reloadableAmount}
          isErrorMsg={isDisabled}
          isGraphQlLoading={isGraphQlLoading}
          onNext={handleAddToCart}
          onAmountChange={handleAmountChange}
          isDrawer={isDrawer}
        />
      )}
      <div
        className={isDrawer ? 'sui-w-full' : containerWidthClass}
        data-testid="reload-buttons-wrapper"
      >
        {!showAmount && (
          <div className={buttonWrapperClass} data-testid="reloadgc-button-wrapper">
            <Button fullWidth variant="primary" onClick={handleShowReload} disabled={isDisabled}>
              {VERBIAGE.reloadGiftCard}
            </Button>
          </div>
        )}

        {!isDrawer && (
          <>
            <div className={buttonWrapperClass}>
              <Button
                fullWidth
                variant="secondary"
                onClick={handleCheckAnotherBalance}
                disabled={disableBtn}
              >
                {VERBIAGE.checkAnotherBalance}
              </Button>
            </div>
            <div
              className={classNames(buttonWrapperClass, 'sui-text-center', {
                'sui-pointer-events-none': disableBtn
              })}
            >
              <Link href={URL.plp}>{VERBIAGE.backToGiftCards}</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

ReloadForm.propTypes = {
  availableAmount: PropTypes.number,
  cardNumber: PropTypes.string,
  reloadableAmount: PropTypes.number,
  errorMsg: PropTypes.string,
  isDrawer: PropTypes.bool,
  cardLast4: PropTypes.string,
  savedPaymentId: PropTypes.string,
  onError: PropTypes.func,
  onCancel: PropTypes.func
};
ReloadForm.defaultProps = {
  availableAmount: 0,
  cardNumber: null,
  reloadableAmount: 0,
  errorMsg: '',
  isDrawer: false,
  cardLast4: '',
  savedPaymentId: '',
  onCancel: () => {},
  onError: () => {}
};

export default ReloadForm;
